import { fetchJSON as fetchAPI } from './client';

export type BusinessObjective = { key: string; name: string; description: string; kpi?: string };
export type CapabilityModule = { key: string; name: string; description?: string; integrations?: string[]; order: number };
export type HealthScoreComponent = {
  key: string; name: string; current_value?: string; target?: string; gap?: string;
  priority?: string; business_impact?: string; ai_explanation?: string;
  evidence?: string[]; recommended_actions?: string[]; weight?: number;
};
export type IntelligenceEngine = {
  key: string; name: string; section?: string; analysis_dimensions?: string[];
  outputs?: string[]; description?: string;
};
export type AttributionFactor = {
  key: string; name: string; source_type: string; metrics?: string[];
  description?: string; measured: boolean;
};
export type FBARecommendation = {
  key: string; title: string; business_objective: string; evidence?: string[];
  expected_revenue_impact: string; expected_profit_impact: string;
  expected_inventory_impact: string; expected_cash_flow_impact: string;
  confidence: number; risk: string; implementation_effort: string;
  observation_window: string; related_experiments?: string[];
  rollback_guidance?: string; category?: string;
};
export type ExperimentIntegrationStep = { key: string; name: string; order: number; description?: string; automated: boolean };
export type UXCapability = { key: string; name: string; description?: string; mode?: string };
export type PerformanceTarget = { key: string; surface: string; max_value: string; unit: string; description?: string };
export type APISpecification = {
  key: string; category: string; endpoints?: string[]; description: string;
  authorization?: string; rate_limit?: string; cache_strategy?: string;
};
export type DatabaseTable = {
  key: string; name: string; purpose: string; columns?: string[]; indexes?: string[]; relationships?: string[];
};
export type TestingRequirement = { key: string; level: string; scope: string; required: boolean; description?: string };
export type AcceptanceCriterion = { key: string; criterion: string; measurable: boolean; target?: string };
export type EngineValidation = {
  valid: boolean; health_engine_ready: boolean; intelligence_ready: boolean;
  attribution_ready: boolean; recommendations_ready: boolean; experiment_integration: boolean;
  performance_met: boolean; acceptance_passed: boolean;
  gaps?: string[]; evaluated_at: string;
};
export type EngineDashboard = {
  prd_id: string; tenant_id: string; version: string; depends_on: string[];
  business_objectives: number; capability_modules: number; health_score_components: number;
  intelligence_engines: number; attribution_factors: number; recommendations: number;
  experiment_steps: number; ux_capabilities: number; performance_targets: number;
  api_specifications: number; database_tables: number; testing_requirements: number;
  acceptance_criteria: number; generated_at: string;
};
export type FGEPRDStatus = {
  prd_id: string; version: string; depends_on: string[];
  business_objectives: number; capability_modules: number; health_score_components: number;
  intelligence_engines: number; attribution_factors: number; recommendations: number;
  experiment_steps: number; ux_capabilities: number; performance_targets: number;
  api_specifications: number; database_tables: number; testing_requirements: number;
  acceptance_criteria: number; financially_justified: boolean; production_ready: boolean; status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const fbaGrowthEngineApi = {
  dashboard: () => fetchMarketIntel<EngineDashboard>('/fge/dashboard'),
  businessObjectives: () => fetchMarketIntel<{ objectives: BusinessObjective[] }>('/fge/business-objectives').then((r) => r.objectives),
  capabilityArchitecture: () => fetchMarketIntel<{ capabilities: CapabilityModule[] }>('/fge/capability-architecture').then((r) => r.capabilities),
  healthEngine: () => fetchMarketIntel<{ components: HealthScoreComponent[] }>('/fge/health-engine').then((r) => r.components),
  intelligenceEngines: () => fetchMarketIntel<{ engines: IntelligenceEngine[] }>('/fge/intelligence-engines').then((r) => r.engines),
  attribution: () => fetchMarketIntel<{ factors: AttributionFactor[] }>('/fge/attribution').then((r) => r.factors),
  recommendations: () => fetchMarketIntel<{ recommendations: FBARecommendation[] }>('/fge/recommendations').then((r) => r.recommendations),
  experimentIntegration: () => fetchMarketIntel<{ steps: ExperimentIntegrationStep[] }>('/fge/experiment-integration').then((r) => r.steps),
  uxCapabilities: () => fetchMarketIntel<{ capabilities: UXCapability[] }>('/fge/ux-capabilities').then((r) => r.capabilities),
  performanceTargets: () => fetchMarketIntel<{ targets: PerformanceTarget[] }>('/fge/performance-targets').then((r) => r.targets),
  apis: () => fetchMarketIntel<{ apis: APISpecification[] }>('/fge/apis').then((r) => r.apis),
  databaseSchema: () => fetchMarketIntel<{ tables: DatabaseTable[] }>('/fge/database-schema').then((r) => r.tables),
  testing: () => fetchMarketIntel<{ requirements: TestingRequirement[] }>('/fge/testing').then((r) => r.requirements),
  acceptanceCriteria: () => fetchMarketIntel<{ criteria: AcceptanceCriterion[] }>('/fge/acceptance-criteria').then((r) => r.criteria),
  validate: (payload: { attrs: Record<string, boolean> }) =>
    fetchMarketIntel<EngineValidation>('/fge/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  status: () => fetchMarketIntel<FGEPRDStatus>('/fge/status'),
};
